import { Form, Input, Modal, Radio, Space, Tabs } from "antd"
import PropertySelector, { PropertySelection } from "../../PropertySelector/PropertySelector"
import { useEffect, useState } from "react"
import { ProjectType, ProjectTypes, projectTypeName } from "../../../common/constants"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPieChart } from "@fortawesome/pro-solid-svg-icons"
import NewCustomerForm from "./NewCustomerForm"
import { CustomerOption, NewCustomerData, allowCreateJob, buildNewJobRequest, createEmptyCustomerData, getIntercomEventData, getJobRedirect } from "./newJobModel"
import { useCreateNewJobMutation } from "./newJobQueries"
import useServiceTicketsEnabled from "../../../hooks/useServiceTicketsEnabled"
import { jobNameMaxLength } from "../../../domain/jobs/jobName"
import useFeatureFlag from "../../../hooks/useFeatureFlag"
import { JOB_DETAILS_CLEANUP_FEATURE } from "../../../domain/jobs/jobs"

export type Props = {
  jobType: ProjectType
  visible: boolean
  onCancel: () => void
  isEstimate?: boolean
  googleMapsApiKey: string
}

const NewJobModal = ({jobType, visible, onCancel, isEstimate, googleMapsApiKey}: Props) => {
  const showServiceTickets = useServiceTicketsEnabled()
  const jobDetailsCleanup = useFeatureFlag(JOB_DETAILS_CLEANUP_FEATURE)
  const [selectedTab, setSelectedTab] = useState('existing')
  const [propertySelection, setPropertySelection] = useState<PropertySelection>()
  const [newCustomerData, setNewCustomerData] = useState<NewCustomerData>(createEmptyCustomerData())
  const [jobCreationType, setJobCreationType] = useState<ProjectType>(jobType)
  const [jobName, setJobName] = useState('')
  useEffect(() => {
    if (visible) {
      setJobName('')
    }
  }, [visible])
  const createJobMutation = useCreateNewJobMutation(response => {
    const intercomData = getIntercomEventData(jobCreationType, response)
    window.Intercom('trackEvent', intercomData.event, intercomData.metadata)
    
    window.location.href = getJobRedirect(jobCreationType, response)
  })
  
  const canCreate = allowCreateJob(selectedTab as CustomerOption, propertySelection, newCustomerData, jobName, jobDetailsCleanup)

  const createNewJob = () => {
    if (!canCreate) {
      return
    }
    const request = buildNewJobRequest(selectedTab as CustomerOption, propertySelection, newCustomerData, jobName)
    createJobMutation.mutate({ projectType: jobCreationType, request })
  }

  return <Modal
    title={<Space>
      <FontAwesomeIcon icon={faPieChart} />
      <span>Create New {isEstimate ? 'Estimate' : projectTypeName(jobCreationType)}</span>
    </Space>}
    open={visible}
    width={900}
    onCancel={() => !createJobMutation.isLoading && onCancel()}
    cancelButtonProps={{ disabled: createJobMutation.isLoading }}
    onOk={createNewJob}
    okText="Create"
    okButtonProps={{ disabled: !canCreate, loading: createJobMutation.isLoading }}
  >
    {isEstimate && <div style={{ margin: '20px 0px' }}>
      <div style={{margin: '5px 0px'}}>Estimate Type</div>
      <Radio.Group
        onChange={e => setJobCreationType(e.target.value)} 
        value={jobCreationType}>
        <Radio value={ProjectTypes.Project}>Project</Radio>
        {showServiceTickets && <Radio value={ProjectTypes.Ticket}>Service Ticket</Radio>}
        <Radio value={ProjectTypes.Maintenance}>Maintenance Contract</Radio>
      </Radio.Group>
    </div>}
    {jobDetailsCleanup && <Form layout="vertical">
      <Form.Item label="Name" htmlFor="new-job-name" required>
        <Input id="new-job-name" value={jobName} maxLength={jobNameMaxLength} placeholder="e.g. Smith backyard patio"
          onChange={e => setJobName(e.target.value)} />
      </Form.Item>
    </Form>}
    <Tabs
      activeKey={selectedTab}
      items={[
        { key: 'existing', label: 'Existing Customer', children: <PropertySelector onChange={setPropertySelection} /> },
        { key: 'new', label: 'New Customer', children: <NewCustomerForm onChange={setNewCustomerData} googleMapsApiKey={googleMapsApiKey} /> }
      ]}
      onChange={setSelectedTab}
    />
  </Modal>
}

export default NewJobModal